import type { WallId } from '@walls/store/types';
import {
  accessTokenKey,
  clearAuthTokens,
  readStorageValue,
  refreshTokenKey,
  writeStorageValue,
} from './storage';

declare global {
  interface Window {
    WALLS_API_BASE_URL?: string;
  }
}

const tokenRefreshWindowMs = 60 * 1000;

export class WallsApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'WallsApiError';
    this.status = status;
  }
}

export class WallsClient {
  getAssetUrl = (path: string) => `${this.resolveApiBaseUrl()}${path}`;

  requestJson = async <T>(
    path: string,
    boardId?: WallId,
    key?: string | null,
    init?: RequestInit,
    retryOnUnauthorized = true
  ): Promise<T> => {
    const headers = {
      'Content-Type': 'application/json',
      ...(await this.getWallHeaders(boardId, key)),
      ...(init?.headers as Record<string, string> | undefined),
    };

    const response = await fetch(this.getAssetUrl(path), {
      ...init,
      headers,
    });

    if (response.status === 401 && retryOnUnauthorized && readStorageValue(refreshTokenKey)) {
      const refreshedToken = await this.refreshAccessToken();

      if (refreshedToken) {
        return this.requestJson<T>(path, boardId, key, init, false);
      }
    }

    if (!response.ok) {
      throw new WallsApiError(response.status, await this.getErrorMessage(response));
    }

    return (await response.json()) as T;
  };

  requestFormJson = async <T>(
    path: string,
    boardId?: WallId,
    key?: string | null,
    body?: BodyInit,
    retryOnUnauthorized = true
  ): Promise<T> => {
    const response = await fetch(this.getAssetUrl(path), {
      method: 'POST',
      headers: await this.getWallHeaders(boardId, key),
      body,
    });

    if (response.status === 401 && retryOnUnauthorized && readStorageValue(refreshTokenKey)) {
      const refreshedToken = await this.refreshAccessToken();

      if (refreshedToken) {
        return this.requestFormJson<T>(path, boardId, key, body, false);
      }
    }

    if (!response.ok) {
      throw new WallsApiError(response.status, await this.getErrorMessage(response));
    }

    return (await response.json()) as T;
  };

  private resolveApiBaseUrl = () => {
    if (typeof window !== 'undefined') {
      const runtimeApiBaseUrl = window.WALLS_API_BASE_URL?.trim();
      if (runtimeApiBaseUrl) {
        return runtimeApiBaseUrl;
      }
    }

    return import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8082';
  };

  private getWallHeaders = async (boardId?: WallId, key?: string | null) => {
    const headers: Record<string, string> = {};
    const token = await this.getCurrentAccessToken();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (boardId) {
      const topicUser = readStorageValue(`dotstorming_topic_user_${boardId}`);
      if (topicUser) {
        headers[`dotstorming_topic_user_${boardId}`] = topicUser;
      }
    }

    if (key?.trim()) {
      headers.key = key.trim();
    }

    return headers;
  };

  private getCurrentAccessToken = async () => {
    const token = readStorageValue(accessTokenKey);

    if (!token) {
      return null;
    }

    if (!this.shouldRefreshToken(token)) {
      return token;
    }

    return (await this.refreshAccessToken()) ?? token;
  };

  private refreshAccessToken = async () => {
    const refreshToken = readStorageValue(refreshTokenKey);

    if (!refreshToken) {
      return null;
    }

    const response = await fetch(this.getAssetUrl('/users/refreshToken'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: refreshToken }),
    });

    if (!response.ok) {
      clearAuthTokens();
      return null;
    }

    const payload = (await response.json()) as {
      token?: { accessToken?: string; refreshToken?: string };
    };
    const nextAccessToken = payload.token?.accessToken?.trim() ?? null;
    const nextRefreshToken = payload.token?.refreshToken?.trim() ?? refreshToken;

    if (!nextAccessToken) {
      clearAuthTokens();
      return null;
    }

    writeStorageValue(accessTokenKey, nextAccessToken);
    writeStorageValue(refreshTokenKey, nextRefreshToken);

    return nextAccessToken;
  };

  private shouldRefreshToken = (token: string) => {
    const expiry = this.parseTokenExpiry(token);

    if (!expiry) {
      return false;
    }

    return expiry - Date.now() <= tokenRefreshWindowMs;
  };

  private parseTokenExpiry = (token: string) => {
    const [, payload] = token.split('.');

    if (!payload) {
      return null;
    }

    const payloadText = this.decodeBase64Url(payload);

    if (!payloadText) {
      return null;
    }

    try {
      const parsedPayload = JSON.parse(payloadText) as { exp?: number };

      if (typeof parsedPayload.exp !== 'number' || !Number.isFinite(parsedPayload.exp)) {
        return null;
      }

      return parsedPayload.exp * 1000;
    } catch {
      return null;
    }
  };

  private decodeBase64Url = (value: string) => {
    try {
      const padded = value.padEnd(Math.ceil(value.length / 4) * 4, '=');
      return atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    } catch {
      return null;
    }
  };

  private getErrorMessage = async (response: Response) => {
    const payload = await response.text();
    return payload.trim() || `Request failed with status ${response.status}`;
  };
}
